import React, { useRef, useLayoutEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Colors } from 'modules/theme';
import Icon from './Icon';

const MENU_MIN_WIDTH = 160;

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Button = styled.div`
  height: 38px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: transparent;
`;

const MenuItems = styled.div`
  display: ${props => (props.isDropdownOpen ? 'block' : 'none')};
  background-color: ${Colors.white};
  min-width: ${MENU_MIN_WIDTH}px;
  position: ${props => (props.usePortal ? 'fixed' : 'absolute')};
  top: ${props => (props.usePortal && props.menuTop != null ? `${props.menuTop}px` : '30px')};
  left: ${props => (props.usePortal && props.menuLeft != null ? `${props.menuLeft}px` : 'auto')};
  right: ${props => (props.usePortal ? 'auto' : '0')};
  z-index: ${props => (props.usePortal ? 10000 : 3)};
  justify-content: flex-start;
  box-shadow: 0 2px 3px 0 rgba(39, 39, 39, 0.08), 0 1px 1px 0 rgba(39, 39, 39, 0.23);
`;

const Box = styled.div`
  position: relative;
  padding: 20px;
`;

const MenuItem = styled.div`
  padding: 10px;
  text-align: left;
  cursor: pointer;

  &: hover {
    opacity: 0.5;
  }
`;

const Title = styled.span`
  font-style: normal;
  font-weight: 350;
  font-size: 14px;
  line-height: 17px;
  color: ${Colors.black};
`;

const Label = styled.span`
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  color: ${props => props.color};
`;

const CloseButton = styled.button`
  pointer-events: all;
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
  outline: none;
`;

const Dropdown = props => {
  const {
    onClickDropdownButton,
    isDropdownOpen,
    options,
    dropdownId,
    title,
    icon,
    iconColor,
    usePortal,
  } = props;

  const containerRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const updateMenuPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el || !isDropdownOpen) return;
    const rect = el.getBoundingClientRect();
    // position:fixed is viewport-relative
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.right - MENU_MIN_WIDTH,
    });
  }, [isDropdownOpen]);

  useLayoutEffect(() => {
    if (!usePortal || !isDropdownOpen) return undefined;
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [usePortal, isDropdownOpen, updateMenuPosition]);

  const menuContent = (
    <MenuItems
      isDropdownOpen={isDropdownOpen}
      usePortal={usePortal}
      menuTop={usePortal ? menuPosition.top : undefined}
      menuLeft={usePortal ? menuPosition.left : undefined}
    >
      <Box>
        <CloseButton onClick={onClickDropdownButton}>
          <Icon name="times" fontSize={10} color={Colors.black} />
        </CloseButton>

        {options.map(({ title: optTitle, icon: optIcon, color, action }, key) => (
          <MenuItem onClick={action} color={color} key={key}>
            {optIcon && (
              <Icon
                name={optIcon}
                color={color || Colors.black}
                customStyle={{ marginRight: '10px' }}
              />
            )}
            {optTitle && <Label color={color || Colors.black}>{optTitle}</Label>}
          </MenuItem>
        ))}
      </Box>
    </MenuItems>
  );

  return (
    <DropdownContainer id={dropdownId} ref={containerRef}>
      <Button onClick={onClickDropdownButton}>
        {icon && <Icon name={icon} color={iconColor || Colors.black} />}
        {title && <Title bold={true}>{title}</Title>}
      </Button>
      {usePortal && isDropdownOpen
        ? createPortal(menuContent, document.body)
        : !usePortal
        ? menuContent
        : null}
    </DropdownContainer>
  );
};

Dropdown.propTypes = {
  onClickDropdownButton: PropTypes.func.isRequired,
  isDropdownOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  /** Renders the menu in a portal with fixed position (avoids overflow:hidden from table scroll containers). */
  usePortal: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      action: PropTypes.func.isRequired,
      icon: PropTypes.string,
      color: PropTypes.string,
    }).isRequired,
  ).isRequired,
  dropdownId: PropTypes.string,
};

Dropdown.defaultProps = {
  usePortal: false,
};

export default Dropdown;
